import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';

import './AvailabilitySummary.css';
import { apiFetch } from '../../utils/apiClient';

/*
    Important: daily and monthly desks are treated a bit differently here.
    Daily desks are selected by date and the user can pick different desks for each day.
    A single monthly desk is selected for all the months the user chooses for the entire period.
*/

const Desk = ({ desk, onSelect, isSelected }) => {

    const idClass = `desk-${desk.id}`;
    const classNames = [
        idClass,
        isSelected ? 'selected' : '',
    ].join(' ').trim();

    return (
        <li key={desk.id} className="desk-item">
            <button
                onClick={() => onSelect(desk)}
                className={classNames}
            >
                Desk {desk.id}
            </button>
        </li>
    );
};

const AvailableDaily = ({ date, desks, onSelectDesk, selectedDesks }) => {

    // the date parameter here is a single date in a one-element array
    // this is required to match the date format for monthly bookings
    // format the date as "Day of the week, Day of the month Month Year"
    const formattedDate = format(parseISO(date), "EEEE do MMMM yyyy");

    return (
        <div className='availability-summary-item desk-wrapper'>
            <h3>{formattedDate}</h3>
            {
                // handle the case when there is no desk available for the date
                !desks.length ? (
                    <p>No desk available for this period.</p>
                ) : (
                    <div className="available-date-panel">
                        <ul className="desk-list">
                            {desks.map((desk) => (
                                <Desk
                                    key={desk.id}
                                    desk={desk}
                                    onSelect={(desk) => onSelectDesk(date, desk)}
                                    isSelected={selectedDesks[date] && selectedDesks[date].id === desk.id}
                                />
                            ))}
                        </ul>
                    </div>
                )
            }
        </div>
    );
};

const AvailableMonthly = ({ availableDesks, onSelectDesk, selectedDesks }) => {

    // availableDesks is an array of objects, each object contains a date and an array of desks
    // however, the desks are the same for each month
    // so we can just use the first month in the list to get the desks
    // the range of months available is shown in the header

    // format the date range as "Month Year - Month Year"

    // get the first month in the list
    // we assume the first month is the same for all months
    // so we use this as the representative month
    const representativeMonth = availableDesks[0]["date"];

    let formattedDate;
    if (availableDesks.length === 1) {
        formattedDate = format(parseISO(availableDesks[0]["date"]), "MMMM yyyy");
    } else {
        formattedDate = format(parseISO(availableDesks[0]["date"]), "MMMM yyyy") + " - " + format(parseISO(availableDesks[availableDesks.length - 1]["date"]), "MMMM yyyy");
    }

    return (
        <div className='availability-summary-item desk-wrapper'>
            <h3>{formattedDate}</h3>
            {
                // handle the case when there is no desk available for the date
                !availableDesks[0]["desks"].length ? (
                    <p>No desk available for this period.</p>
                ) : (
                    <div className="available-date-panel">
                        <ul className="desk-list">
                            {availableDesks[0]["desks"].map((desk) => (
                                // only need to take the first month as the desks are the same for all months
                                // when passing the desk to the onSelect function, we pass the entire array of months
                                // so that we can update the selected desks for all months                                
                                <Desk
                                    key={desk.id}
                                    desk={desk}
                                    onSelect={(desk) => onSelectDesk(availableDesks, desk.id)}
                                    // can just check the first month in the list, as the desk ID is the same for all months
                                    isSelected={selectedDesks[representativeMonth] && selectedDesks[representativeMonth].id === desk.id}
                                />
                            ))}
                        </ul>
                    </div>
                )
            }
        </div>
    );
};

const AvailabilitySummary = ({
    bookingType,
    bookingDates,
    setFinalDesks,
    setTotalPrice,
    resetAfterTimeOutOrError,
    isRenewal = false,
    group_booking_id = null
}) => {

    // local development uses env.development, production routes to /api
    const API_URL = process.env.REACT_APP_API_URL || "/api";

    const [availableDesks, setAvailableDesks] = useState([]);
    const [selectedDesks, setSelectedDesks] = useState({});

    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
    const [apiErrorMessage, setApiErrorMessage] = useState('');

    useEffect(() => {

        // reset the selected desks when the booking dates change
        setSelectedDesks({});
        setTotalPrice(0);

        if (!bookingDates || bookingDates.length === 0) {
            return;
        }
        // check if the booking dates are valid

        const fetchAvailableDesks = async () => {
            try {

                const payload = {
                    booking_dates: bookingDates,
                    booking_type: bookingType,
                    renewal: isRenewal,
                    // #208: excludes this customer's own in-flight pending booking from the
                    // reserved set, so it isn't shown as unavailable against themselves.
                    ...(group_booking_id && { group_booking_id }),
                }

                const deskAvailabilityURL = `${API_URL}/desks/availability`;

                const response = await apiFetch(deskAvailabilityURL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(`${data.error}`);
                }

                setAvailableDesks(data);

            } catch (error) {
                // catch any errors, i.e. ones not caught by the fetch call
                // and also the one thrown by the fetch call
                setApiErrorMessage(error.message);
                setIsErrorModalOpen(true);
            }
        };

        fetchAvailableDesks();
        // group_booking_id is deliberately NOT a dependency here, even though it's read above --
        // this effect's job is "the criteria changed, start desk selection over" (that's what
        // the resets at the top are for), and a booking being created is not that. Adding it as
        // a trigger made this whole effect re-run the instant a booking exists, wiping
        // selectedDesks -> cascading into setFinalDesks({}) below -> deleting the booking that
        // was just created. The value read above is still always current: effects close over
        // the render they were scheduled in regardless of what's listed here, and every date/
        // type change that legitimately re-triggers this effect already resets group_booking_id
        // to null in that same batched render (see the Reset*Change functions in BookingForm.js).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingDates, bookingType, API_URL, setTotalPrice, isRenewal]);


    const handleDailySelectDesk = (date, desk) => {


        // the date parameter here is a single date
        setSelectedDesks((prevSelectedDesks) => {

            const updatedDesks = { ...prevSelectedDesks };

            if (updatedDesks[date] && updatedDesks[date].id === desk.id) {
                delete updatedDesks[date];
            } else {
                updatedDesks[date] = desk;
            }

            // If no desks are selected (i.e deselection of a desk), 
            // return an empty object
            return Object.keys(updatedDesks).length === 0 ? {} : updatedDesks;

        });
    }

    const handleMonthlySelectDesk = (availableDesks, deskID) => {

        // we assume the user can only pick one desk for the entire period
        // so we apply the same desk ID to each month
        setSelectedDesks((prevSelectedDesks) => {

            const updatedDesks = { ...prevSelectedDesks };

            // loop over each date (i.e. month) in the availableDesks array
            // and update the selected desk for each month
            // this is to ensure that the selected desk is the same for all months
            availableDesks.forEach((d) => {
                const date = d["date"];
                // check if the desk is already selected for the date
                // if it is, then we need to remove it
                // if it is not, then we need to add it
                if (updatedDesks[date] && updatedDesks[date].id === deskID) {
                    delete updatedDesks[d];
                } else {
                    updatedDesks[date] = {
                        id: deskID,
                        price: d.desks.find((desk) => desk.id === deskID).price,
                    };
                }
            });

            // If no desks are selected (i.e deselection of a desk), 
            // return an empty object
            return Object.keys(updatedDesks).length === 0 ? {} : updatedDesks;
        });

    };


    // update the form data with the selected desks
    useEffect(() => {

        // update the form data with the selected desks

        // check selected desks are not empty
        if (Object.keys(selectedDesks).length >= 0) {

            // calculate the total price
            let totalPrice = 0;
            for (const date in selectedDesks) {
                totalPrice += selectedDesks[date].price;
            }

            // update the form data with the selected desks and total price
            setTotalPrice(totalPrice);
            setFinalDesks(selectedDesks);

        }

    }, [selectedDesks, setFinalDesks, setTotalPrice]);


    return (
        <div>
            <div className='availability-summary-container'>
                {bookingType === "daily" && availableDesks.map((day) => (
                    <AvailableDaily
                        key={day.date}
                        date={day.date}
                        desks={day.desks}
                        onSelectDesk={handleDailySelectDesk}
                        selectedDesks={selectedDesks}
                    />
                ))}
                {bookingType === "monthly" && Array.isArray(availableDesks) && availableDesks.length > 0 && (
                    <AvailableMonthly
                        availableDesks={availableDesks}
                        onSelectDesk={handleMonthlySelectDesk}
                        selectedDesks={selectedDesks}
                    />
                )}
            </div>
            {isErrorModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <p>
                            Sorry, something went wrong. Please refresh the page and try again.
                        </p>
                        <p className='error-code'>
                            {apiErrorMessage}
                        </p>
                        <button onClick={() => {
                            setIsErrorModalOpen(false);
                            resetAfterTimeOutOrError();
                        }}>
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div >
    );
};

export default AvailabilitySummary;